import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiModuleShopRecommendations } from '@wix/da-papi-types';
import { MeasuredSlider as Slider } from '@wix/da-shared-react/pkg/Slider';
import SellPromotionCard from '@wix/da-shared-react/pkg/ShopCard/SellPromotionCard';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiData, UserSymbolLearnMoreClickBiEvent } from '@wix/da-bi/pkg/events';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import { sanitizeUrl } from '@wix/da-url';
import ShopRecommendationsMobile from './ShopRecommendationsMobile';
import useModuleData from '@wix/da-gruser-shared/pkg/hooks/useModuleData';

import s from './ShopRecommendations.scss';

interface Props {
  moduleId: number;
}

const ShopRecommendations: FC<Props> = ({ moduleId }) => {
  const isMobile = useContext(MobileContext);
  const { moduleData } = useModuleData<PapiModuleShopRecommendations>(moduleId);
  const { deviations } = moduleData;
  const { t } = useTranslation();

  if (!deviations) {
    return null;
  }

  const learnMoreLink = sanitizeUrl(
    t('widgets.shopRecommendations.header.cta.link')
  );
  const learnMoreBiData = BiData<UserSymbolLearnMoreClickBiEvent>({
    evid: 112,
    link_url: learnMoreLink,
    link_name: 'user-shop-recommendations',
  });

  return (
    <div className={s['root']}>
      {isMobile ? (
        <>
          <div className={s['header']}>
            {t('widgets.shopRecommendations.header')}
          </div>
          <div className={s['subheader']}>
            {t('widgets.shopRecommendations.subheader.withCta.mobile', {
              postProcess: ['reactPostprocessor'],
              learnMoreLink: (
                <BiLink
                  className={s['header-cta']}
                  target="_blank"
                  rel="noreferrer"
                  href={learnMoreLink}
                  biData={learnMoreBiData}
                >
                  {t('widgets.shopRecommendations.header.cta.mobile')}
                </BiLink>
              ),
            })}
          </div>
        </>
      ) : (
        <div className={s['header-container']}>
          <div className={s['header']}>
            {t('widgets.shopRecommendations.header')}
          </div>
          <div className={s['learn-more-link']}>
            <TextButton
              size="medium"
              variant="main"
              target="_blank"
              rel="noreferrer"
              href={learnMoreLink}
              biData={learnMoreBiData}
            >
              {t('widgets.shopRecommendations.header.cta')}
            </TextButton>
          </div>
          <div className={s['subheader']}>
            {t('widgets.shopRecommendations.subheader')}
          </div>
        </div>
      )}
      {isMobile ? (
        <ShopRecommendationsMobile deviations={deviations} />
      ) : (
        <Slider
          className={s['slider']}
          renderSliderItems={elementSize => (
            <>
              {deviations.map((deviation, index) => (
                <SellPromotionCard
                  key={index}
                  deviation={deviation}
                  width={elementSize.width}
                />
              ))}
            </>
          )}
        />
      )}
    </div>
  );
};
ShopRecommendations.displayName = 'ShopRecommendationsWithLink';

export default ShopRecommendations;
